import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { QRCodeSVG } from 'qrcode.react';
import Layout from '../components/Layout';
import restaurantConfig from '../config/restaurantConfig';
import { useMerchant } from '../context/MerchantContext';
import { useAuth } from '../context/AuthContext';
import { isRestaurantOpen as checkOpen, getNextOpeningTime as getNextTime } from '../utils/restaurantUtils';

const HeroSection = styled.section`
  background-image: linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('https://images.unsplash.com/photo-1504674900247-0877df9cc836?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80');
  background-size: cover;
  background-position: center;
  height: 500px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  color: white;
  padding: 0 1rem;
  margin: 0 -1rem 2rem -1rem;
  
  @media (min-width: 993px) {
    margin-top: -1rem;
  }
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
`;

const HeroSubtitle = styled.p`
  font-size: 1.5rem;
  margin-bottom: 2rem;
  max-width: 800px;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
`;

const HeroButton = styled(Link)`
  background-color: #e74c3c;
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-size: 1.1rem;
  font-weight: 500;
  text-decoration: none;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #c0392b;
  }
`;

const FeaturesSection = styled.section`
  padding: 3rem 0;
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  text-align: center;
  margin-bottom: 3rem;
  color: #333;

  &::after {
    content: '';
    display: block;
    width: 50px;
    height: 3px;
    background-color: #e74c3c;
    margin: 0.5rem auto 0;
  }
`;


const FeatureCard = styled.div`
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  text-align: center;
  transition: transform 0.3s ease;
  height: 100%;

  &:hover {
    transform: translateY(-10px);
  }
`;

const FeatureIcon = styled.div`
  font-size: 3rem;
  color: #e74c3c;
  margin-bottom: 1rem;
`;

const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #333;
`;

const FeatureDescription = styled.p`
  color: #666;
  line-height: 1.6;
`;

const HowItWorksSection = styled.section`
  padding: 3rem 0;
  background-color: #f9f9f9;
  margin: 3rem -1rem;
  padding: 3rem 1rem;
`;

const Step = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  height: 100%;
`;

const StepNumber = styled.div`
  background-color: #e74c3c;
  color: white;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 1rem;
`;

const StepTitle = styled.h3`
  font-size: 1.3rem;
  margin-bottom: 0.5rem;
  color: #333;
`;

const StepDescription = styled.p`
  color: #666;
  line-height: 1.6;
`;

const QRCodeSection = styled.section`
  padding: 3rem 0;
  background-color: #fff;
  text-align: center;
`;

const QRCodeContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  max-width: 600px;
  margin: 0 auto;
  padding: 2rem;
  background-color: #f9f9f9;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const QRCodeImage = styled.div`
  width: 200px;
  height: 200px;
  background-color: #fff;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const QRCodeTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #333;
`;

const QRCodeDescription = styled.p`
  color: #666;
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const SelectorContainer = styled.div`
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SelectorLabel = styled.p`
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 0.25rem;
`;

const LanguageSelector = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const QRCodeTypeSelector = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const SelectorButton = styled.button<{ active: boolean }>`
  padding: 0.5rem 1rem;
  border: 1px solid ${props => props.active ? '#e74c3c' : '#ddd'};
  background-color: ${props => props.active ? '#e74c3c' : 'transparent'};
  color: ${props => props.active ? 'white' : '#666'};
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${props => props.active ? '#c0392b' : '#f1f1f1'};
  }
`;

const LanguageButton = styled(SelectorButton)``;
const QRCodeTypeButton = styled(SelectorButton)``;

const QRCodeValue = styled.div`
  font-size: 0.8rem;
  color: #666;
  margin-top: 0.5rem;
  word-break: break-all;
  background-color: #f9f9f9;
  padding: 0.5rem;
  border-radius: 4px;
  border: 1px solid #eee;
`;

const CTASection = styled.section`
  text-align: center;
  padding: 3rem 0;
`;

const CTATitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 1rem;
  color: #333;
`;

const CTADescription = styled.p`
  font-size: 1.2rem;
  color: #666;
  margin-bottom: 2rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

const CTAButton = styled(Link)`
  background-color: #e74c3c;
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-size: 1.1rem;
  font-weight: 500;
  text-decoration: none;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #c0392b;
  }
`;

// Restaurant Info Section
const RestaurantInfoSection = styled.section`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 1.5rem;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1.5rem;
`;

const RestaurantMainInfo = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 300px;
`;

const RestaurantLogo = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 1.5rem;
`;

const RestaurantInfoContent = styled.div`
  flex: 1;
`;

const RestaurantName = styled.h2`
  font-size: 1.5rem;
  margin-bottom: 0.25rem;
  color: #333;
`;

const RestaurantRating = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 0.5rem;
`;

const RatingStars = styled.div`
  color: #fbbf24;
  display: flex;
  align-items: center;
  font-size: 0.9rem;
`;

const RatingValue = styled.span`
  font-weight: 700;
  color: #333;
  font-size: 0.95rem;
`;

const ReviewCount = styled.span`
  color: #718096;
  font-size: 0.85rem;
`;

const RestaurantStatus = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: #666;
`;

const StatusBadge = styled.span<{ isOpen: boolean }>`
  color: ${props => props.isOpen ? '#4CAF50' : '#F44336'};
  font-weight: 500;
`;

const StatusDot = styled.span<{ isOpen: boolean }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${props => props.isOpen ? '#4CAF50' : '#F44336'};
  margin-right: 0.25rem;
`;

const InfoLink = styled(Link)`
  color: #e74c3c;
  text-decoration: none;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

const CashbackCard = styled.div`
  display: flex;
  background-color: #fff;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  flex: 1;
  min-width: 300px;
`;

const CashbackIcon = styled.div`
  background-color: #27ae60;
  color: white;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  margin-right: 1.2rem;
  flex-shrink: 0;
`;

const CashbackContent = styled.div`
  flex: 1;
`;

const CashbackTitle = styled.h3`
  font-size: 1.1rem;
  color: #2c3e50;
  margin: 0 0 0.8rem 0;
  font-weight: 600;
`;

const CashbackText = styled.p`
  font-size: 0.95rem;
  color: #555;
  line-height: 1.5;
  margin: 0 0 0.8rem 0;

  strong {
    color: #333;
    font-weight: 700;
  }
`;

const CashbackLink = styled.span`
  font-size: 0.95rem;
  color: #555;
  line-height: 1.5;
  
  a {
    color: #2c3e50;
    font-weight: 700;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const HomePage: React.FC = () => {
  const { restaurant } = useMerchant();
  const { isAuthenticated } = useAuth();
  const [language, setLanguage] = useState<'pt' | 'en' | 'es'>('pt');
  const [qrCodeType, setQrCodeType] = useState<'menu' | 'table' | 'counter' | 'schedule'>('menu');
  const [isRestaurantOpen, setIsRestaurantOpen] = useState<boolean>(false);
  const [nextOpeningTime, setNextOpeningTime] = useState<string>('');

  // Check if restaurant is open
  useEffect(() => {
    const updateRestaurantStatus = () => {
      const open = checkOpen(restaurant);
      setIsRestaurantOpen(open);
      
      if (!open) {
        const nextTime = getNextTime(restaurant);
        // Extract only the time if it's "Hoje às HH:MM", or keep it as is
        const timeOnly = nextTime.includes('Hoje às ') ? nextTime.replace('Hoje às ', '') : nextTime;
        setNextOpeningTime(timeOnly);
      }
    };

    updateRestaurantStatus();
    // Check every minute
    const interval = setInterval(updateRestaurantStatus, 60000);
    return () => clearInterval(interval);
  }, [restaurant]);

  // Function to handle language change
  const handleLanguageChange = (lang: 'pt' | 'en' | 'es') => {
    setLanguage(lang);
  };

  // Function to handle QR code type change
  const handleQrCodeTypeChange = (type: 'menu' | 'table' | 'counter' | 'schedule') => {
    setQrCodeType(type);
  };

  // QR code URL based on selected language and purpose
  const getQRCodeUrl = (purpose = 'menu') => {
    const baseUrl = window.location.origin;

    switch (purpose) {
      case 'table':
        // For table-specific QR code (example: table 1)
        return `${baseUrl}/table-menu/default/1?lang=${language}`;
      case 'counter':
        // For counter service QR code
        return `${baseUrl}/counter-menu/default?lang=${language}`;
      case 'schedule':
        // For scheduled orders QR code
        return `${baseUrl}/schedule-order/default?lang=${language}`;
      case 'menu':
      default:
        // Default menu QR code
        return `${baseUrl}/menu?lang=${language}`;
    }
  };

  return (
    <Layout showRestaurantInfo={false}>
      <HeroSection>
        <HeroTitle>{restaurantConfig.restaurant.name} - Cardápio Digital</HeroTitle>
        <HeroSubtitle>
          {restaurantConfig.restaurant.description}
        </HeroSubtitle>
        <HeroButton to="/menu">Ver Cardápio</HeroButton>
      </HeroSection>

      <RestaurantInfoSection>
        <RestaurantMainInfo>
          <RestaurantLogo src={restaurant?.logo || restaurantConfig.images.logo} alt={restaurant?.name || restaurantConfig.restaurant.name} />
          <RestaurantInfoContent>
            <RestaurantName>{restaurant?.name || restaurantConfig.restaurant.name}</RestaurantName>
            
            {restaurant?.rating !== undefined && restaurant?.rating > 0 && (
              <RestaurantRating>
                <RatingStars>
                  <i className="fas fa-star"></i>
                </RatingStars>
                <RatingValue>{restaurant.rating.toFixed(1)}</RatingValue>
                {restaurant.reviewCount !== undefined && restaurant.reviewCount > 0 && (
                  <ReviewCount>({restaurant.reviewCount} {restaurant.reviewCount === 1 ? 'avaliação' : 'avaliações'})</ReviewCount>
                )}
              </RestaurantRating>
            )}

            <RestaurantStatus>
              <StatusBadge isOpen={isRestaurantOpen}>
                <StatusDot isOpen={isRestaurantOpen} />
                {isRestaurantOpen ? 'Aberto agora' : 'Fechado'}
              </StatusBadge>
              {!isRestaurantOpen && (
                <span>• Abrimos às {nextOpeningTime}</span>
              )}
              {restaurant?.address && (
                <span>• {restaurant.address.city} - {restaurant.address.state}</span>
              )}
              <span>• <InfoLink to={`/restaurant/${restaurant?.id || 'default'}`}>Mais informações</InfoLink></span>
            </RestaurantStatus>
          </RestaurantInfoContent>
        </RestaurantMainInfo>

        {restaurant?.cashbackConfig?.enabled && (
          <CashbackCard>
            <CashbackIcon>
              <i className="fas fa-coins"></i>
            </CashbackIcon>
            <CashbackContent>
              <CashbackTitle>Sistema de Cashback</CashbackTitle>
              <CashbackText>
                Ganhe <strong>{restaurant.cashbackConfig.type === 'PERCENTAGE' ? `${restaurant.cashbackConfig.value}%` : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(restaurant.cashbackConfig.value)}</strong> de volta em suas compras!
              </CashbackText>
              <CashbackLink>
                <Link to="/cashback">
                  {isAuthenticated ? 'Clique aqui para ver seu saldo e como funciona.' : 'Clique aqui para saber mais sobre nosso sistema de cashback.'}
                </Link>
              </CashbackLink>
            </CashbackContent>
          </CashbackCard>
        )}
      </RestaurantInfoSection>

      <FeaturesSection>
        <SectionTitle>Por que escolher nosso cardápio digital?</SectionTitle>
        <div className="row g-4">
          <div className="col-12 col-md-6 col-lg-4">
            <FeatureCard>
              <FeatureIcon>🍽️</FeatureIcon>
              <FeatureTitle>Cardápio Completo</FeatureTitle>
              <FeatureDescription>
                Acesse nosso cardápio completo com fotos, descrições detalhadas e preços atualizados.
              </FeatureDescription>
            </FeatureCard>
          </div>

          <div className="col-12 col-md-6 col-lg-4">
            <FeatureCard>
              <FeatureIcon>🚀</FeatureIcon>
              <FeatureTitle>Pedidos Rápidos</FeatureTitle>
              <FeatureDescription>
                Faça seus pedidos em poucos cliques, sem precisar esperar atendimento.
              </FeatureDescription>
            </FeatureCard>
          </div>

          <div className="col-12 col-md-6 col-lg-4">
            <FeatureCard>
              <FeatureIcon>📱</FeatureIcon>
              <FeatureTitle>Acessível em Qualquer Dispositivo</FeatureTitle>
              <FeatureDescription>
                Acesse nosso cardápio digital pelo celular, tablet ou computador, a qualquer momento.
              </FeatureDescription>
            </FeatureCard>
          </div>

          <div className="col-12 col-md-6 col-lg-4">
            <FeatureCard>
              <FeatureIcon>🔍</FeatureIcon>
              <FeatureTitle>Informações Nutricionais</FeatureTitle>
              <FeatureDescription>
                Consulte informações nutricionais detalhadas e identificação de alérgenos em cada item do cardápio.
              </FeatureDescription>
            </FeatureCard>
          </div>

          <div className="col-12 col-md-6 col-lg-4">
            <FeatureCard>
              <FeatureIcon>⭐</FeatureIcon>
              <FeatureTitle>Avaliações e Comentários</FeatureTitle>
              <FeatureDescription>
                Veja avaliações de outros clientes e deixe sua opinião sobre os pratos que experimentou.
              </FeatureDescription>
            </FeatureCard>
          </div>

          <div className="col-12 col-md-6 col-lg-4">
            <FeatureCard>
              <FeatureIcon>🌐</FeatureIcon>
              <FeatureTitle>Múltiplos Idiomas</FeatureTitle>
              <FeatureDescription>
                Acesse o cardápio em diferentes idiomas para uma experiência personalizada.
              </FeatureDescription>
            </FeatureCard>
          </div>
        </div>
      </FeaturesSection>

      <HowItWorksSection>
        <SectionTitle>Como Funciona</SectionTitle>
        <div className="row g-4">
          <div className="col-12 col-sm-6 col-lg-3">
            <Step>
              <StepNumber>1</StepNumber>
              <StepTitle>Escolha seus itens</StepTitle>
              <StepDescription>
                Navegue pelo nosso cardápio digital e adicione os itens desejados ao seu carrinho.
              </StepDescription>
            </Step>
          </div>

          <div className="col-12 col-sm-6 col-lg-3">
            <Step>
              <StepNumber>2</StepNumber>
              <StepTitle>Personalize seu pedido</StepTitle>
              <StepDescription>
                Adicione observações, escolha opções e personalize seu pedido conforme sua preferência.
              </StepDescription>
            </Step>
          </div>

          <div className="col-12 col-sm-6 col-lg-3">
            <Step>
              <StepNumber>3</StepNumber>
              <StepTitle>Confirme e pague</StepTitle>
              <StepDescription>
                Revise seu pedido, escolha a forma de pagamento e confirme para finalizar.
              </StepDescription>
            </Step>
          </div>

          <div className="col-12 col-sm-6 col-lg-3">
            <Step>
              <StepNumber>4</StepNumber>
              <StepTitle>Receba seu pedido</StepTitle>
              <StepDescription>
                Acompanhe o status do seu pedido e receba-o no conforto da sua casa ou retire no local.
              </StepDescription>
            </Step>
          </div>
        </div>
      </HowItWorksSection>

      <QRCodeSection>
        <SectionTitle>Acesse Nosso Cardápio via QR Code</SectionTitle>
        <QRCodeContainer>
          <QRCodeImage>
            <QRCodeSVG 
              value={getQRCodeUrl(qrCodeType)}
              size={150}
              bgColor={"#ffffff"}
              fgColor={"#e74c3c"}
              level={"L"}
              includeMargin={false}
            />
          </QRCodeImage>
          <QRCodeTitle>Escaneie e acesse o cardápio no seu celular</QRCodeTitle>
          <QRCodeDescription>
            Aponte a câmera do seu smartphone para este QR Code e acesse nosso cardápio digital completo.
            Compartilhe com amigos ou salve para acessar facilmente no futuro.
          </QRCodeDescription>

          <SelectorContainer>
            <div>
              <SelectorLabel>Tipo de QR Code:</SelectorLabel>
              <QRCodeTypeSelector>
                <QRCodeTypeButton 
                  active={qrCodeType === 'menu'} 
                  onClick={() => handleQrCodeTypeChange('menu')}
                >
                  Cardápio Geral
                </QRCodeTypeButton>
                <QRCodeTypeButton 
                  active={qrCodeType === 'table'} 
                  onClick={() => handleQrCodeTypeChange('table')}
                >
                  Mesa
                </QRCodeTypeButton>
                <QRCodeTypeButton 
                  active={qrCodeType === 'counter'} 
                  onClick={() => handleQrCodeTypeChange('counter')}
                >
                  Balcão
                </QRCodeTypeButton>
                <QRCodeTypeButton 
                  active={qrCodeType === 'schedule'} 
                  onClick={() => handleQrCodeTypeChange('schedule')}
                >
                  Agendamento
                </QRCodeTypeButton>
              </QRCodeTypeSelector>
            </div>

            <div>
              <SelectorLabel>Idioma:</SelectorLabel>
              <LanguageSelector>
                <LanguageButton 
                  active={language === 'pt'} 
                  onClick={() => handleLanguageChange('pt')}
                >
                  Português
                </LanguageButton>
                <LanguageButton 
                  active={language === 'en'} 
                  onClick={() => handleLanguageChange('en')}
                >
                  English
                </LanguageButton>
                <LanguageButton 
                  active={language === 'es'} 
                  onClick={() => handleLanguageChange('es')}
                >
                  Español
                </LanguageButton>
              </LanguageSelector>
            </div>

            <QRCodeValue>
              {getQRCodeUrl(qrCodeType)}
            </QRCodeValue>
          </SelectorContainer>
        </QRCodeContainer>
      </QRCodeSection>

      <CTASection>
        <CTATitle>Pronto para experimentar?</CTATitle>
        <CTADescription>
          Faça seu pedido agora mesmo através do nosso cardápio digital e desfrute de uma experiência gastronômica incrível.
        </CTADescription>
        <CTAButton to="/menu">Fazer Pedido</CTAButton>
      </CTASection>
    </Layout>
  );
};

export default HomePage;
